import type { DutyAnalyticsDto } from '@pams/shared';
import { ReportSection, type SectionState } from '@/components/ReportSection';
import {
  TOTAL_CELL,
  ZERO_ATTENDANCE,
  cellFor,
  divisionPageHref,
  joinFigureRows,
  rowKeyOf,
  sectionData,
  type DutyAnalyticsWindowParam,
  type FigureRow,
  type GroupedFigures,
} from '@/lib/dutyAnalytics';
import { DUTY_ANALYTICS_COPY } from '../copy';
import { DELEGATION_INFO, MARKINGS_INFO, tableInfo } from '../figureInfo';
import { DelegationShape } from './DelegationShape';
import { DivisionPicker } from './DivisionPicker';
import { FigureInfo } from './FigureInfo';
import { FiguresNotes } from './FiguresNotes';
import { FiguresTable, type FiguresTableRow } from './FiguresTable';
import { HeadlineCards } from './HeadlineCards';
import { MarkingsBand } from './MarkingsBand';
import { SectionFailure } from './SectionFailure';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [P7C §2c] THE COMMISSIONERATE'S PAGE — a static layout, chosen because the SERVER echoed this
 *  layout, never because of who is signed in or what the tile registry holds.
 *
 *  Order: the four cards; Divisions, with an "All divisions" total; Units, narrowed by a division
 *  picker; Delegation; Markings; what each figure counts. [amended P7C.1 §3] That last card is hidden
 *  (`SURFACES.dutyAnalyticsFigureNotes`); each card and section carries its own info button instead, and
 *  both tables' dialogs carry the layout note.
 *
 *  ⚠️ THE ROOT'S OWN ROW IS WORDED AS THE COMMISSIONERATE'S OWN WORK. Force can be asked of the
 *  commissionerate itself (`isCommissionerate`); that row is not a division, so it is named for what it
 *  is, and it is kept out of the picker and the delegation bars.
 *
 *  ⚠️ [amended P7C.1 §4] A DIVISION'S NAME IN THE DIVISIONS TABLE OPENS THAT DIVISION'S OWN PAGE — and only
 *  there: never the root's own row, never a row the server does not place at division level
 *  (`isDivisionLevel`, by POSITION — a battalion labelled DIVISION gets none), never the Units table, the
 *  markings or the bars. The server refuses the page to anyone not at the commissionerate; the link is only
 *  where it is offered. It keeps the window and this page's narrowing for the way back.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

const copy = DUTY_ANALYTICS_COPY;
const PREFIX = 'duty-analytics';

const nameOf = (row: FigureRow): string => row.identity.orgUnit?.name ?? copy.unknownUnit;

export const CommissionerateLayout = ({
  report,
  range,
  division,
  onDivision,
}: {
  report: DutyAnalyticsDto;
  range: DutyAnalyticsWindowParam;
  division: string;
  onDivision: (next: string | null) => void;
}) => {
  const fill = sectionData(report.fill);
  const delegation = sectionData(report.delegation);
  const attendance = sectionData(report.attendance);

  const divisionRows = joinFigureRows(report.rows, {
    fill: fill === null ? null : fill.rows,
    delegation: delegation === null ? null : delegation.rows,
    attendance: attendance === null ? null : attendance.rows,
  });
  const unitRows = joinFigureRows(report.units ?? [], {
    fill: fill === null ? null : (fill.units ?? []),
    delegation: delegation === null ? null : (delegation.units ?? []),
    attendance: attendance === null ? null : (attendance.units ?? []),
  });
  const totals: GroupedFigures = {
    fill: fill?.total ?? null,
    delegation: delegation?.total ?? null,
    attendance: attendance?.total ?? null,
  };

  const divisionNames = new Map(report.rows.map((row) => [row.orgUnitId, row.orgUnit?.name ?? copy.unknownUnit]));

  const divisionTable: FiguresTableRow[] = divisionRows.map((row) => ({
    key: rowKeyOf(row.identity),
    orgUnitId: row.identity.orgUnitId,
    label: row.identity.isCommissionerate ? copy.commissionerateOwnRow(nameOf(row)) : nameOf(row),
    figures: row.figures,
    cell: cellFor('COMMISSIONERATE', 'rows', row.identity),
    nameHref:
      !row.identity.isCommissionerate && row.identity.orgUnit !== null && row.identity.isDivisionLevel
        ? divisionPageHref(row.identity.orgUnitId, range, division)
        : undefined,
  }));
  const unitTable: FiguresTableRow[] = unitRows.map((row) => ({
    key: rowKeyOf(row.identity),
    orgUnitId: row.identity.orgUnitId,
    label: nameOf(row),
    secondary: row.identity.isDivisionItself
      ? copy.divisionItselfSecondary
      : row.identity.divisionId === null
        ? ''
        : (divisionNames.get(row.identity.divisionId) ?? copy.unknownUnit),
    figures: row.figures,
    cell: cellFor('COMMISSIONERATE', 'units', row.identity),
  }));

  const realDivisions = divisionRows.filter((row) => !row.identity.isCommissionerate);
  /* The delegation picture needs both halves of each bar; it fails with either. */
  const delegationState: SectionState<FigureRow[]> =
    fill !== null && delegation !== null ? { data: realDivisions } : { failure: 'UNAVAILABLE' };

  return (
    <>
      <HeadlineCards
        cards={fill?.cards ?? null}
        layout="COMMISSIONERATE"
        range={range}
        pageDivision={division}
        scopeLabel={copy.allDivisions}
      />

      <ReportSection
        id="divisions"
        testIdPrefix={PREFIX}
        title={copy.divisionsTitle}
        state={report.fill}
        loadingLabel={copy.loadingSection(copy.divisionsTitle)}
        failure={<SectionFailure />}
        aside={<FigureInfo infoKey="DIVISIONS" title={copy.divisionsTitle} content={tableInfo(true)} />}
      >
        {() => (
          <FiguresTable
            testId="duty-analytics-divisions"
            labelledBy={`${PREFIX}-divisions`}
            nameHeader={copy.divisionHeader}
            rows={divisionTable}
            footer={[
              {
                kind: 'linked',
                testId: 'duty-analytics-all-divisions',
                label: copy.allDivisions,
                figures: totals,
                cell: TOTAL_CELL,
              },
            ]}
            range={range}
            pageDivision={division}
            empty={copy.noRows}
          />
        )}
      </ReportSection>

      <ReportSection
        id="units"
        testIdPrefix={PREFIX}
        title={copy.unitsTitle}
        state={report.fill}
        loadingLabel={copy.loadingSection(copy.unitsTitle)}
        failure={<SectionFailure />}
        aside={<FigureInfo infoKey="UNITS" title={copy.unitsTitle} content={tableInfo(true)} />}
        actions={
          <DivisionPicker rows={report.rows} value={division} narrowedTo={report.narrowedTo} onChange={onDivision} />
        }
      >
        {() => (
          <FiguresTable
            testId="duty-analytics-units"
            labelledBy={`${PREFIX}-units`}
            nameHeader={copy.unitHeader}
            secondaryHeader={copy.askedThroughHeader}
            rows={unitTable}
            range={range}
            pageDivision={division}
            empty={copy.noUnits}
          />
        )}
      </ReportSection>

      <ReportSection
        id="delegation"
        testIdPrefix={PREFIX}
        title={copy.delegationTitle}
        state={delegationState}
        loadingLabel={copy.loadingSection(copy.delegationTitle)}
        failure={<SectionFailure />}
        aside={<FigureInfo infoKey="DELEGATION" title={copy.delegationTitle} content={DELEGATION_INFO} />}
      >
        {(rows) => (
          <DelegationShape
            nameHeader={copy.divisionHeader}
            rows={rows.map((row) => ({
              orgUnitId: row.identity.orgUnitId,
              name: nameOf(row),
              asked: row.figures.fill?.asked ?? 0,
              handedDown: row.figures.delegation?.delegated ?? 0,
            }))}
          />
        )}
      </ReportSection>

      <ReportSection
        id="markings"
        testIdPrefix={PREFIX}
        title={copy.markingsTitle}
        state={report.attendance}
        loadingLabel={copy.loadingSection(copy.markingsTitle)}
        failure={<SectionFailure />}
        aside={<FigureInfo infoKey="MARKINGS" title={copy.markingsTitle} content={MARKINGS_INFO} />}
      >
        {(section) => (
          <MarkingsBand
            nameHeader={copy.divisionHeader}
            range={range}
            pageDivision={division}
            rows={divisionRows.map((row) => ({
              key: rowKeyOf(row.identity),
              orgUnitId: row.identity.orgUnitId,
              label: row.identity.isCommissionerate ? copy.commissionerateOwnRow(nameOf(row)) : nameOf(row),
              figures: row.figures.attendance ?? ZERO_ATTENDANCE,
              cell: cellFor('COMMISSIONERATE', 'rows', row.identity),
            }))}
            footer={{
              key: 'total',
              orgUnitId: null,
              label: copy.allDivisions,
              figures: section.total,
              cell: TOTAL_CELL,
              testId: 'duty-analytics-markings-total',
            }}
          />
        )}
      </ReportSection>

      <FiguresNotes layout="COMMISSIONERATE" />
    </>
  );
};
